import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { CourseListItemAdmin, CreateCourseRequest } from '../../../../../lib/interfaces/courses';
import { UserJob } from '../../../../../lib/interfaces/user';
import { Button } from '../../../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../../../ui/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../../ui/components/ui/form';
import { Input } from '../../../../../ui/components/ui/input';
import AudienceSelector from '../audience-selector/audience-selector';
import CoverUploadField from '../cover-upload-field/cover-upload-field';

const courseFormSchema = z
  .object({
    title: z.string().trim().min(3, 'Mindestens 3 Zeichen').max(120, 'Maximal 120 Zeichen'),
    allJobs: z.boolean(),
    userJobs: z.array(z.nativeEnum(UserJob))
  })
  .refine((data) => data.allJobs || data.userJobs.length > 0, {
    message: 'Wähle mindestens eine Rolle oder „Alle Rollen“.',
    path: ['userJobs']
  });

type CourseFormValues = z.infer<typeof courseFormSchema>;

export interface CourseFormSubmitPayload {
  data: CreateCourseRequest;
  file: File | null;
  removeExistingImage: boolean;
}

/** Only the fields the form actually reads — a list item and a course detail both satisfy this. */
export type CourseFormSource = Pick<CourseListItemAdmin, 'title' | 'audience' | 'imageUrl'>;

export interface CourseFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  course?: CourseFormSource | null;
  onSubmit: (payload: CourseFormSubmitPayload) => Promise<void>;
}

const buildDefaults = (course: CourseFormSource | null | undefined): CourseFormValues => ({
  title: course?.title ?? '',
  allJobs: course?.audience.allJobs ?? false,
  userJobs: course?.audience.userJobs ?? []
});

const CourseFormDialog = ({ isOpen, onOpenChange, course, onSubmit }: CourseFormDialogProps) => {
  const isEdit = !!course;
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<CourseFormValues>({
    resolver: zodResolver(courseFormSchema),
    defaultValues: buildDefaults(course)
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(buildDefaults(course));
    setPickedFile(null);
    setRemoveExistingImage(false);
  }, [isOpen, course, form]);

  const handleSubmit = async (values: CourseFormValues) => {
    setIsSubmitting(true);
    try {
      const payload: CourseFormSubmitPayload = {
        data: {
          title: values.title.trim(),
          audience: values.allJobs ? { allJobs: true, userJobs: [] } : { allJobs: false, userJobs: values.userJobs }
        },
        file: pickedFile,
        removeExistingImage
      };
      await onSubmit(payload);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const existingImageUrl = !removeExistingImage ? course?.imageUrl ?? null : null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md min-w-[520px] py-4 px-6 gap-5 bg-white" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-[25px] font-bold text-[#12282A]">{isEdit ? 'Kurs bearbeiten' : 'Neuer Kurs'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Name des Kurses *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="z. B. Grundlagen" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="userJobs"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Zielgruppe *</FormLabel>
                  <FormControl>
                    <AudienceSelector
                      value={{ allJobs: form.watch('allJobs'), userJobs: field.value }}
                      onChange={(next) => {
                        form.setValue('allJobs', next.allJobs, { shouldDirty: true });
                        field.onChange(next.userJobs);
                      }}
                    />
                  </FormControl>
                  {fieldState.error && <FormMessage>{fieldState.error.message}</FormMessage>}
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-[10px]">
              <span className="text-[14px] font-bold text-[#12282A]">Cover-Bild</span>
              <CoverUploadField
                existingImageUrl={existingImageUrl}
                onChange={setPickedFile}
                onRemoveExisting={isEdit ? () => setRemoveExistingImage(true) : undefined}
              />
            </div>

            <DialogFooter className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Speichert…' : isEdit ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CourseFormDialog;
